/**
 * SKU Status Classification — Inventory UI Labels
 * ────────────────────────────────────────────────
 * Lightweight, UI-facing labels for SKUs flowing through the warehouse.
 * Status drives badges and tooltips only; it does NOT alter inventory,
 * receiving, or allocation behavior.
 *
 * Status taxonomy:
 *   • canonical  — appears in a canonical JobSpec BOM / approved catalog.
 *   • imported   — real migrated inventory SKU (e.g. from CCC) not yet
 *                  mapped to a JobSpec BOM. Reserved for future migration.
 *   • deprecated — old demo / seed SKU, kept for historical fixtures only.
 *                  Not intended for future use; do not extend.
 *   • unmapped   — unknown SKU with no canonical / imported / deprecated
 *                  match. Surfaces as a warning so silent strangers are
 *                  visible.
 *
 * This module is read-only validation. See src/data/inventory/CANONICAL.md.
 */

import { JOB_SPECS } from "@/data/jobSpec";

/** All SKUs declared by canonical JobSpec BOMs. */
export function getJobSpecCanonicalSkus(): Set<string> {
  const set = new Set<string>();
  for (const spec of JOB_SPECS) {
    if (spec.status !== "canonical") continue;
    for (const item of spec.billOfMaterials) set.add(item.sku);
  }
  return set;
}

/** True if a SKU exists in any canonical JobSpec BOM. */
export function isCanonicalSku(sku: string): boolean {
  return getJobSpecCanonicalSkus().has(sku);
}

/**
 * Imported SKUs — real migrated inventory items (e.g. CCC physical stock)
 * that are not yet mapped to a canonical JobSpec BOM. Empty during beta;
 * populated when real migration begins. Adding a SKU here marks it as a
 * legitimate, recognized inventory item without claiming canonical status.
 */
export const IMPORTED_SKUS: ReadonlySet<string> = new Set<string>([]);

export function isImportedSku(sku: string): boolean {
  return IMPORTED_SKUS.has(sku);
}

/**
 * Deprecated SKUs — pre-JobSpec demo / seed items kept only so historical
 * fixtures continue to render. They are NOT a migration target; they exist
 * to keep beta seed data internally consistent. Do not add new entries.
 */
export const DEPRECATED_SKUS: ReadonlySet<string> = new Set<string>([
  "WALL-SEN-STD",
  "WALL-DEK-STD",
  "WALL-TIL-STD",
  "BASE-SEN-3060",
  "BASE-DEK-CUST",
  "GLS-CLR-STD",
  "GLS-SLD-KIT",
  "FIX-STD-CHR",
  "ACC-GRAB-12",
  "ACC-GRAB-18",
  "ACC-GRAB-24",
  "ACC-SEAT-FLD",
  "ACC-CADDY-SS",
  "DRN-LIN-STD",
  "WP-MEM-KIT",
]);

export function isDeprecatedSku(sku: string): boolean {
  return DEPRECATED_SKUS.has(sku);
}

export type SkuStatus = "canonical" | "imported" | "deprecated" | "unmapped";

/** Back-compat alias. Prefer `SkuStatus` in new code. */
export type SkuClassification = SkuStatus;

export function classifySku(sku: string): SkuStatus {
  if (isCanonicalSku(sku)) return "canonical";
  if (isImportedSku(sku)) return "imported";
  if (isDeprecatedSku(sku)) return "deprecated";
  return "unmapped";
}

/**
 * Given a list of SKUs (e.g. from a PO, receiving record, or stock catalog),
 * return those with no recognized status. These are the most dangerous —
 * silent strangers in the inventory namespace.
 */
export function findUnmappedSkus(skus: Iterable<string>): string[] {
  const out: string[] = [];
  for (const sku of skus) {
    if (classifySku(sku) === "unmapped") out.push(sku);
  }
  return out;
}
